/**
 * 物品详情抽屉（与 CharacterDetailDrawer 同构，配色用 amber 区分）
 *
 * - 当前主图预览
 * - 生成新候选（async ImageJob）：选模型 + 视角模板 + 提示词
 * - 5 秒轮询拉最新候选
 * - 候选网格：设为主图 / 删除
 * - 顶部右上角：从作品库导入
 */

import { useEffect, useRef, useState } from 'react';
import {
  Loader2,
  Plus,
  Package,
  Trash2,
  X,
  Check,
  FolderOpen,
  Cpu,
  Wand2,
  ZoomIn,
  Upload,
  Image as ImageIcon,
} from 'lucide-react';
import toast from '../../../../components/Toast';
import {
  directorApi,
  DirectorApiError,
  type DirectorProp,
  type DirectorImageJob,
} from '../../services/directorApi';
import { api as imageApi } from '../../../../services/api';
import type { ModelConfig } from '../../../../services/api';
import { ImportFromLibraryDialog } from '../library/ImportFromLibraryDialog';
import { Lightbox } from '../../../../baselines/image/Lightbox';
import { withAuthToken } from '../../../../utils/storageUrl';
import {
  DIRECTOR_IMAGE_SIZE_OPTIONS,
  getDefaultDirectorImageSize,
  type DirectorAspectRatio,
  type DirectorImageSize,
} from '../../utils/imageSize';

interface Props {
  open: boolean;
  propId: string;
  projectAspectRatio: DirectorAspectRatio;
  onClose: () => void;
  onPropUpdated: () => void;
}

const SECTION_LABEL =
  'flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500';
const FIELD =
  'w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-amber-300 focus:ring-2 focus:ring-amber-100';

export function PropDetailDrawer({
  open,
  propId,
  projectAspectRatio,
  onClose,
  onPropUpdated,
}: Props) {
  const [prop, setProp] = useState<DirectorProp | null>(null);
  const [images, setImages] = useState<DirectorImageJob[]>([]);
  const [loading, setLoading] = useState(false);

  const [models, setModels] = useState<ModelConfig[]>([]);
  const [modelId, setModelId] = useState('');
  const [angleTemplates, setAngleTemplates] = useState<
    Array<{ name: string; promptSuffix: string }>
  >([]);
  const [angleTemplate, setAngleTemplate] = useState('');
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState<DirectorImageSize>(() =>
    getDefaultDirectorImageSize(projectAspectRatio)
  );
  const [quality, setQuality] = useState<'low' | 'medium' | 'high'>('low');
  const [generating, setGenerating] = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  /** 参考图（已存在的 ImageJob ID）：选中后生成走 img2img */
  const [referenceImageJobId, setReferenceImageJobId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const handleUploadFile = async (file: File) => {
    setUploading(true);
    try {
      await directorApi.uploadPropPortrait(propId, file);
      toast.success('已上传并设为主图');
      reload();
      onPropUpdated();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const reload = async () => {
    if (!propId) return;
    setLoading(true);
    try {
      const [p, imgs] = await Promise.all([
        directorApi.getProp(propId),
        directorApi.listPropImages(propId),
      ]);
      setProp(p);
      setImages(imgs);
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSize(getDefaultDirectorImageSize(projectAspectRatio));
    reload();
    imageApi
      .getModels()
      .then((d) => {
        setModels(d.data);
        if (d.data.length > 0 && !modelId) setModelId(d.data[0].id);
      })
      .catch(() => undefined);
    directorApi
      .getPropAngleTemplates()
      .then((list) => {
        setAngleTemplates(list);
        setAngleTemplate((prev) => {
          if (prev && list.some((t) => t.name === prev)) return prev;
          return list[0]?.name ?? '';
        });
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, propId, projectAspectRatio]);

  useEffect(() => {
    if (!open) return;
    const inFlight = images.some((img) =>
      ['pending', 'submitting', 'processing'].includes(img.status)
    );
    if (!inFlight) return;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, open]);

  if (!open) return null;

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('请填写提示词');
      return;
    }
    if (!modelId) {
      toast.error('请选择图片模型');
      return;
    }
    if (!angleTemplate) {
      toast.error('请选择视角');
      return;
    }
    setGenerating(true);
    try {
      const result = await directorApi.generatePropImage(propId, {
        prompt,
        modelId,
        angleTemplateName: angleTemplate,
        size,
        quality,
        referenceImageJobId: referenceImageJobId ?? undefined,
      });
      toast.success(`生成中（消耗 ${result.creditsUsed} 积分）`);
      setPrompt('');
      setReferenceImageJobId(null);
      reload();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '生成失败');
    } finally {
      setGenerating(false);
    }
  };

  const handleSetPortrait = async (imageJobId: string) => {
    try {
      await directorApi.updateProp(propId, { portraitImageJobId: imageJobId });
      toast.success('已设为主图');
      reload();
      onPropUpdated();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '设置失败');
    }
  };

  const handleDeleteImage = async (imageJobId: string) => {
    if (!window.confirm('确定要删除这张候选图吗？')) return;
    try {
      await directorApi.deleteKeyframe(imageJobId);
      toast.success('已删除');
      reload();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '删除失败');
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-slate-950/55 backdrop-blur-sm">
        <button
          type="button"
          aria-label="关闭"
          className="absolute inset-0 cursor-default"
          onClick={onClose}
        />
        <div
          className="relative flex h-full w-full max-w-xl flex-col overflow-hidden bg-[#eef3f8] shadow-2xl shadow-slate-950/30"
          onClick={(e) => e.stopPropagation()}
        >
          {/* 顶部 hero */}
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 px-5 py-4 text-white">
            <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-amber-400/20 blur-2xl" />
            <div className="relative flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                  <Package className="h-3 w-3 text-amber-200" />
                  物品详情
                </div>
                <h2 className="truncate text-lg font-semibold leading-tight">
                  {prop?.name ?? '加载中…'}
                </h2>
                {prop?.description && (
                  <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-slate-300/90">
                    {prop.description}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUploadFile(f);
                    if (uploadInputRef.current) uploadInputRef.current.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => uploadInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 rounded-2xl bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                  title="从本地上传主图"
                >
                  {uploading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Upload className="h-3.5 w-3.5" />
                  )}
                  上传图片
                </button>
                <button
                  type="button"
                  onClick={() => setShowImport(true)}
                  className="inline-flex items-center gap-1.5 rounded-2xl bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/20"
                  title="从作品库导入主图"
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                  作品库导入
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {loading || !prop ? (
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
            </div>
          ) : (
            <div className="flex-1 space-y-5 overflow-y-auto p-5">

              <section className="rounded-3xl border border-white/70 bg-white/85 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-xl">
                <h3 className="mb-2 text-sm font-semibold text-slate-900">当前主图</h3>
                {prop.portraitImageJobId ? (
                  (() => {
                    const portrait = images.find((i) => i.id === prop.portraitImageJobId);
                    const portraitUrl = portrait?.resultUrl ?? null;
                    return (
                      <button
                        type="button"
                        onClick={() => portraitUrl && setPreviewImage(portraitUrl)}
                        disabled={!portraitUrl}
                        className="group relative aspect-square w-32 overflow-hidden rounded-2xl bg-slate-100 ring-2 ring-amber-500 transition disabled:cursor-default"
                        title={portraitUrl ? '点击放大查看' : ''}
                      >
                        {portraitUrl ? (
                          <>
                            <img
                              src={withAuthToken(portraitUrl)}
                              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                              alt={prop.name}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-slate-950/40 opacity-0 transition group-hover:opacity-100">
                              <ZoomIn className="h-6 w-6 text-white" />
                            </div>
                          </>
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-slate-100">
                            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                          </div>
                        )}
                      </button>
                    );
                  })()
                ) : (
                  <div className="text-sm text-slate-500">未设置主图，请生成或从作品库导入</div>
                )}
              </section>

              <section className="space-y-3 rounded-3xl border border-white/70 bg-white/85 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-xl">
                <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                  <Wand2 className="h-4 w-4 text-amber-500" /> 生成新候选
                </h3>

                <div className="space-y-1.5">
                  <label className={SECTION_LABEL}>
                    <Cpu className="h-3 w-3" /> 图片模型
                  </label>
                  <select value={modelId} onChange={(e) => setModelId(e.target.value)} className={FIELD}>
                    <option value="">选择模型</option>
                    {models.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className={SECTION_LABEL}>视角</label>
                  <select
                    value={angleTemplate}
                    onChange={(e) => setAngleTemplate(e.target.value)}
                    className={FIELD}
                    disabled={angleTemplates.length === 0}
                  >
                    {angleTemplates.length === 0 ? (
                      <option value="">加载中…</option>
                    ) : (
                      angleTemplates.map((t) => (
                        <option key={t.name} value={t.name}>
                          {t.name}
                        </option>
                      ))
                    )}
                  </select>
                  <p className="text-[11px] text-slate-400">
                    选「主图」会作为后续分镜的默认参考；其他视角作为补充候选。
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <label className={SECTION_LABEL}>尺寸</label>
                    <div className="grid grid-cols-3 gap-1">
                      {DIRECTOR_IMAGE_SIZE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setSize(opt.value as typeof size)}
                          className={`rounded-xl border px-2 py-1.5 text-xs font-medium leading-none transition ${
                            size === opt.value
                              ? 'border-slate-950 bg-slate-950 text-white shadow-sm'
                              : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-400">
                      默认跟随项目比例；多视图展示通常更适合 16:9，可按当前用途切换。
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <label className={SECTION_LABEL}>画质</label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { value: 'low', label: '标准' },
                        { value: 'medium', label: '高清' },
                        { value: 'high', label: '超清' },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setQuality(opt.value as typeof quality)}
                          className={`rounded-2xl border px-2 py-2 text-sm font-medium transition ${
                            quality === opt.value
                              ? 'border-slate-950 bg-slate-950 text-white shadow-md'
                              : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-400">画质越高积分越多。</p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className={SECTION_LABEL}>提示词</label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value.slice(0, 1000))}
                    placeholder={`描述物品画面，例如"${prop.name}，白色背景，居中产品图"`}
                    rows={3}
                    className={FIELD}
                  />
                  <p className="text-[11px] text-slate-400">会自动拼接物品描述和模板后缀</p>
                </div>

                {/* 参考图（可选）：选中后切到 img2img 模式 */}
                <div className="space-y-1.5">
                  <label className={SECTION_LABEL}>
                    <ImageIcon className="h-3 w-3" /> 参考图（可选 · 命中后走图生图）
                  </label>
                  {(() => {
                    const completedImages = images.filter(
                      (img) => img.status === 'completed' && !!img.resultUrl
                    );
                    if (completedImages.length === 0) {
                      return (
                        <p className="rounded-2xl bg-slate-50 px-3 py-2 text-[11px] text-slate-400">
                          下方候选图为空时不可选；可先上传 / 生成 / 从作品库导入一张
                        </p>
                      );
                    }
                    return (
                      <div className="flex gap-1.5 overflow-x-auto py-1">
                        <button
                          type="button"
                          onClick={() => setReferenceImageJobId(null)}
                          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl text-[10px] font-medium transition ${
                            referenceImageJobId === null
                              ? 'bg-slate-900 text-white shadow-sm'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                          title="不使用参考图（文生图）"
                        >
                          不用
                        </button>
                        {completedImages.map((img) => {
                          const selected = referenceImageJobId === img.id;
                          return (
                            <button
                              key={img.id}
                              type="button"
                              onClick={() =>
                                setReferenceImageJobId(selected ? null : img.id)
                              }
                              className={`relative h-14 w-14 shrink-0 overflow-hidden rounded-xl ring-1 transition ${
                                selected
                                  ? 'ring-2 ring-amber-500 ring-offset-2 ring-offset-white'
                                  : 'ring-slate-200 hover:ring-amber-300'
                              }`}
                              title="选为参考图"
                            >
                              <img
                                src={img.resultUrl ? withAuthToken(img.resultUrl) : undefined}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                              {selected && (
                                <div className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 shadow-sm">
                                  <Check className="h-2.5 w-2.5 text-white" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generating || !prompt.trim() || !modelId || !angleTemplate}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  生成
                </button>
              </section>

              {images.length > 0 && (
                <section className="rounded-3xl border border-white/70 bg-white/85 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-xl">
                  <h3 className="mb-2 text-sm font-semibold text-slate-900">候选图（{images.length}）</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {images.map((img) => {
                      const isPortrait = img.id === prop.portraitImageJobId;
                      const canZoom = img.status === 'completed' && !!img.resultUrl;
                      return (
                        <div
                          key={img.id}
                          className={`group relative aspect-square overflow-hidden rounded-2xl bg-slate-50 transition ${
                            isPortrait
                              ? 'ring-2 ring-amber-500 ring-offset-2 ring-offset-white'
                              : 'ring-1 ring-slate-200'
                          }`}
                        >
                          {img.status === 'completed' && img.resultUrl ? (
                            <img
                              src={withAuthToken(img.resultUrl)}
                              alt=""
                              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                              loading="lazy"
                            />
                          ) : img.status === 'failed' ? (
                            <div className="flex h-full w-full items-center justify-center bg-rose-50 text-xs text-rose-500">
                              失败
                            </div>
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-slate-100">
                              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                            </div>
                          )}

                          {img.status === 'completed' && (
                            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-slate-950/45 opacity-0 transition group-hover:opacity-100">
                              {canZoom && (
                                <button
                                  type="button"
                                  onClick={() => setPreviewImage(img.resultUrl!)}
                                  className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-amber-50"
                                  title="放大查看"
                                >
                                  <ZoomIn className="h-4 w-4 text-slate-700" />
                                </button>
                              )}
                              {!isPortrait && (
                                <button
                                  type="button"
                                  onClick={() => handleSetPortrait(img.id)}
                                  className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-amber-50"
                                  title="设为主图"
                                >
                                  <Check className="h-4 w-4 text-amber-600" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteImage(img.id)}
                                className="rounded-full bg-white p-1.5 shadow-md transition hover:bg-rose-50"
                                title="删除"
                              >
                                <Trash2 className="h-4 w-4 text-rose-500" />
                              </button>
                            </div>
                          )}

                          {isPortrait && (
                            <div className="absolute right-1.5 top-1.5 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white shadow-md">
                              主图
                            </div>
                          )}

                          {img.usageRole === 'prop_angle' && (
                            <div className="absolute left-1.5 top-1.5 rounded-full bg-slate-950/80 px-2 py-0.5 text-[10px] font-medium text-white">
                              视角
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>

      {prop && (
        <ImportFromLibraryDialog
          open={showImport}
          target={{ type: 'prop', propId, targetLabel: prop.name }}
          onClose={() => setShowImport(false)}
          onImported={() => {
            reload();
            onPropUpdated();
          }}
        />
      )}

      <Lightbox
        image={previewImage ? withAuthToken(previewImage) : null}
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </>
  );
}
